import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useUserCompany } from '../hooks/useUserCompany';
import { useEntity } from '../contexts/EntityContext';
import { useOverdueInvoices } from '../hooks/queries/useOverdueInvoices';
import { useOverdueBills } from '../hooks/queries/useOverdueBills';
import EntitySwitcher from './EntitySwitcher';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onExpandChange?: (expanded: boolean) => void;
}

interface NavIconProps {
  className?: string;
  strokeWidth?: number;
}

const DashboardIcon = ({ className = 'w-5 h-5', strokeWidth = 1.8 }: NavIconProps) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const TransactionsIcon = ({ className = 'w-5 h-5', strokeWidth = 1.8 }: NavIconProps) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const InvoicesIcon = ({ className = 'w-5 h-5', strokeWidth = 1.8 }: NavIconProps) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
  </svg>
);

const BillsIcon = ({ className = 'w-5 h-5', strokeWidth = 1.8 }: NavIconProps) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ChatIcon = ({ className = 'w-5 h-5', strokeWidth = 1.8 }: NavIconProps) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const ReportsIcon = ({ className = 'w-5 h-5', strokeWidth = 1.8 }: NavIconProps) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const SettingsIcon = ({ className = 'w-5 h-5', strokeWidth = 1.8 }: NavIconProps) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const SignOutIcon = ({ className = 'w-5 h-5', strokeWidth = 1.8 }: NavIconProps) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const PartnersIcon = ({ className = 'w-5 h-5', strokeWidth = 1.8 }: NavIconProps) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

// All nav items in order (no section labels)
const navItems = [
  { path: '/dashboard', labelKey: 'navigation.dashboard', Icon: DashboardIcon, tourKey: undefined },
  { path: '/dashboard/chat', labelKey: 'navigation.chat', Icon: ChatIcon, tourKey: 'ai-chat' },
  { path: '/dashboard/transactions', labelKey: 'navigation.transactions', Icon: TransactionsIcon, tourKey: 'transactions' },
  { path: '/dashboard/invoices', labelKey: 'navigation.invoices', Icon: InvoicesIcon, tourKey: 'invoices' },
  { path: '/dashboard/bills', labelKey: 'navigation.bills', Icon: BillsIcon, tourKey: 'bills' },
  { path: '/dashboard/reports', labelKey: 'navigation.insights', Icon: ReportsIcon, tourKey: 'reports' },
  { path: '/dashboard/settings', labelKey: 'navigation.settings', Icon: SettingsIcon, tourKey: 'settings' },
];

const COLLAPSED_WIDTH = 76;
const EXPANDED_WIDTH = 300;
const ADMIN_EMAIL = 'marcomendoza@avilapeakpartners.com';

export default function Sidebar({ isOpen, onClose, onExpandChange }: SidebarProps) {
  const location = useLocation();
  const { t } = useTranslation('common');
  const { user, signOut } = useAuth();
  const { company } = useUserCompany();
  const { activeCompanyId } = useEntity();
  const [hovered, setHovered] = useState(false);
  const [entityDropdownOpen, setEntityDropdownOpen] = useState(false);
  const { summary: overdueInvoicesSummary } = useOverdueInvoices(activeCompanyId ?? undefined);
  const { summary: overdueBillsSummary } = useOverdueBills(activeCompanyId ?? undefined);
  const attentionCount =
    ((overdueInvoicesSummary?.totalOverdue || 0) > 0 ? 1 : 0) +
    ((overdueBillsSummary?.totalOverdue || 0) > 0 ? 1 : 0);

  // Open when hovered on desktop, mobile overlay open, or entity dropdown open
  const open = hovered || isOpen || entityDropdownOpen;

  const handleHoverStart = () => {
    setHovered(true);
    onExpandChange?.(true);
  };

  const handleHoverEnd = () => {
    if (!entityDropdownOpen) {
      setHovered(false);
      onExpandChange?.(false);
    }
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const initials = (company?.user_name || 'U')
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[190] lg:hidden"
          style={{ background: 'rgba(0,0,0,0.4)' }}
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <motion.aside
        className="group/sidebar fixed inset-y-0 left-0 z-[200] flex flex-col overflow-hidden"
        style={{
          background: 'var(--bg-surface)',
          borderRight: '1px solid var(--border-cyan-10)',
        }}
        animate={{ width: open ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}
        transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
        onHoverStart={handleHoverStart}
        onHoverEnd={handleHoverEnd}
        data-sidebar-open={isOpen}
        data-entity-dropdown-open={entityDropdownOpen}
      >
        <div className="flex flex-col h-full py-5 px-3" style={{ width: EXPANDED_WIDTH, flexShrink: 0 }}>

          {/* Logo */}
          <div className="flex items-center mb-6 px-1" style={{ height: 40 }}>
            <div style={{ width: 38, minWidth: 38, display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
              <svg viewBox="0 0 32 32" width="26" height="26" fill="none">
                <path
                  d="M3 16 L7 16 L10 9 L14 23 L18 12 L22 16 L29 16"
                  stroke="var(--cyan)"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <motion.div
              animate={{ opacity: open ? 1 : 0, display: open ? 'block' : 'none' }}
              transition={{ duration: 0.12 }}
              style={{ paddingLeft: 10, overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              <span style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '18px',
                fontWeight: 700,
                color: 'var(--cyan)',
                letterSpacing: '-0.3px',
                lineHeight: 1,
              }}>
                Pulse
              </span>
            </motion.div>
          </div>

          {/* Entity switcher */}
          <div style={{ marginBottom: 8 }}>
            <EntitySwitcher
              sidebarExpanded={open}
              onOpenChange={(val) => {
                setEntityDropdownOpen(val);
                if (!val && !hovered) onExpandChange?.(false);
                if (val) onExpandChange?.(true);
              }}
            />
          </div>

          {/* Nav items */}
          <nav className="flex-1 overflow-y-auto min-h-0 space-y-[2px]" data-tour="navigation">
            {navItems.map((item) => {
              const Icon = item.Icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  data-tour={item.tourKey}
                  className="flex items-center rounded-lg transition-colors duration-100"
                  style={{
                    padding: '4px 4px',
                    // Full-row bg only when expanded
                    background: active && open ? 'var(--nav-active-bg)' : 'transparent',
                    color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                    textDecoration: 'none',
                    fontFamily: 'var(--font-sans)',
                  }}
                  onMouseEnter={(e) => {
                    if (open) {
                      // Expanded: highlight full row
                      e.currentTarget.style.background = active ? 'var(--nav-active-bg)' : 'var(--nav-hover-bg)';
                      e.currentTarget.style.color = 'var(--text-primary)';
                      // Also clear icon bg
                      const iconSpan = e.currentTarget.querySelector('.nav-icon-wrap') as HTMLElement;
                      if (iconSpan) iconSpan.style.background = 'transparent';
                    } else {
                      // Collapsed: highlight icon only
                      const iconSpan = e.currentTarget.querySelector('.nav-icon-wrap') as HTMLElement;
                      if (iconSpan && !active) iconSpan.style.background = 'var(--nav-hover-bg)';
                      e.currentTarget.style.color = 'var(--text-primary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (open) {
                      e.currentTarget.style.background = active ? 'var(--nav-active-bg)' : 'transparent';
                      e.currentTarget.style.color = active ? 'var(--text-primary)' : 'var(--text-secondary)';
                    } else {
                      const iconSpan = e.currentTarget.querySelector('.nav-icon-wrap') as HTMLElement;
                      if (iconSpan && !active) iconSpan.style.background = 'transparent';
                      e.currentTarget.style.color = active ? 'var(--text-primary)' : 'var(--text-secondary)';
                    }
                  }}
                >
                  {/* Icon — always visible; bg lives here when collapsed */}
                  <span
                    className="nav-icon-wrap"
                    style={{
                      width: 44,
                      height: 44,
                      minWidth: 44,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexShrink: 0,
                      borderRadius: 10,
                      // Icon-level bg when collapsed
                      background: active && !open ? 'var(--nav-active-icon)' : 'transparent',
                      color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                      transition: 'background 0.12s ease',
                    }}
                  >
                    <Icon className="w-5 h-5" strokeWidth={active ? 2 : 1.8} />
                  </span>

                  {/* Label — animates in */}
                  <motion.span
                    animate={{
                      display: open ? 'inline-block' : 'none',
                      opacity: open ? 1 : 0,
                    }}
                    transition={{ duration: 0.1 }}
                    className="text-sm whitespace-pre group-hover/sidebar:translate-x-0.5 transition-transform duration-150"
                    style={{ fontWeight: active ? 600 : 400, overflow: 'hidden' }}
                  >
                    {t(item.labelKey as any)}
                  </motion.span>

                  {/* Attention badge */}
                  {item.path === '/dashboard' && attentionCount > 0 && (
                    <motion.span
                      animate={{ display: open ? 'inline-flex' : 'none', opacity: open ? 1 : 0 }}
                      style={{
                        marginLeft: 'auto',
                        background: '#DC2626',
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: 700,
                        minWidth: 18,
                        height: 18,
                        borderRadius: 9,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 5px',
                        lineHeight: 1,
                        marginRight: 4,
                      }}
                    >
                      {attentionCount}
                    </motion.span>
                  )}
                </Link>
              );
            })}

            {/* Admin-only link */}
            {user?.email === ADMIN_EMAIL && (() => {
              const adminActive = location.pathname === '/dashboard/admin/partners';
              return (
              <Link
                to="/dashboard/admin/partners"
                onClick={onClose}
                className="flex items-center rounded-lg transition-colors duration-100"
                style={{
                  padding: '4px 4px',
                  marginTop: 4,
                  background: adminActive && open ? 'var(--nav-active-bg)' : 'transparent',
                  color: adminActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => {
                  if (open) {
                    e.currentTarget.style.background = adminActive ? 'var(--nav-active-bg)' : 'var(--nav-hover-bg)';
                    e.currentTarget.style.color = 'var(--text-primary)';
                  } else {
                    const iconSpan = e.currentTarget.querySelector('.nav-icon-wrap') as HTMLElement;
                    if (iconSpan && !adminActive) iconSpan.style.background = 'var(--nav-hover-bg)';
                    e.currentTarget.style.color = 'var(--text-primary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (open) {
                    e.currentTarget.style.background = adminActive ? 'var(--nav-active-bg)' : 'transparent';
                    e.currentTarget.style.color = adminActive ? 'var(--text-primary)' : 'var(--text-secondary)';
                  } else {
                    const iconSpan = e.currentTarget.querySelector('.nav-icon-wrap') as HTMLElement;
                    if (iconSpan && !adminActive) iconSpan.style.background = 'transparent';
                    e.currentTarget.style.color = adminActive ? 'var(--text-primary)' : 'var(--text-secondary)';
                  }
                }}
              >
                <span
                  className="nav-icon-wrap"
                  style={{
                    width: 44, height: 44, minWidth: 44,
                    display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0,
                    borderRadius: 10,
                    background: adminActive && !open ? 'var(--nav-active-icon)' : 'transparent',
                    color: adminActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                    transition: 'background 0.12s ease',
                  }}
                >
                  <PartnersIcon className="w-5 h-5" />
                </span>
                <motion.span
                  animate={{ display: open ? 'inline-block' : 'none', opacity: open ? 1 : 0 }}
                  transition={{ duration: 0.1 }}
                  className="text-sm whitespace-pre"
                  style={{ fontWeight: 400 }}
                >
                  Partners
                </motion.span>
              </Link>
              );
            })()}
          </nav>

          {/* User section */}
          <div style={{ borderTop: '1px solid var(--user-section-border)', paddingTop: 12 }}>
            {/* User row */}
            <Link
              to="/dashboard/settings"
              style={{ textDecoration: 'none' }}
              onClick={onClose}
            >
              <div
                className="flex items-center rounded-lg transition-colors duration-100"
                style={{ padding: '8px 8px', cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--nav-hover-bg)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {/* Avatar */}
                <div style={{
                  width: 38,
                  minWidth: 38,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexShrink: 0,
                }}>
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'var(--avatar-bg)',
                    color: 'var(--cyan)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '12px',
                    letterSpacing: '0.03em',
                    flexShrink: 0,
                  }}>
                    {initials}
                  </div>
                </div>
                {/* Name + email */}
                <motion.div
                  animate={{ display: open ? 'block' : 'none', opacity: open ? 1 : 0 }}
                  transition={{ duration: 0.1 }}
                  style={{ paddingLeft: 10, overflow: 'hidden' }}
                >
                  <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-body)', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                    {company?.user_name || 'User'}
                  </p>
                  <p style={{ fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160, lineHeight: 1.3 }}>
                    {company?.company_name || 'Organization'}
                  </p>
                </motion.div>
              </div>
            </Link>

            {/* Sign Out */}
            <button
              onClick={() => { signOut?.(); onClose(); }}
              className="w-full flex items-center rounded-lg transition-colors duration-100"
              style={{
                padding: '8px 8px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-sans)',
                marginTop: 2,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--nav-hover-bg)';
                e.currentTarget.style.color = 'var(--text-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
            >
              <span style={{ width: 38, minWidth: 38, display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                <SignOutIcon className="w-5 h-5" />
              </span>
              <motion.span
                animate={{ display: open ? 'inline-block' : 'none', opacity: open ? 1 : 0 }}
                transition={{ duration: 0.1 }}
                className="text-sm whitespace-pre"
              >
                Sign Out
              </motion.span>
            </button>
          </div>
        </div>
      </motion.aside>
    </>
  );
}
